import * as XLSX from 'xlsx';
import type { SchoolCreateRequest } from '../modules/school/types';
import { isStandardSchoolLevel, normalizeSchoolLevel } from '../modules/school/constants';

const MAX_LISTED_ROWS = 10;

export interface ExcelParseResult {
  success: boolean;
  data: SchoolCreateRequest[];
  errors: string[];
  warnings: string[];
}

export interface ExcelRowData {
  [key: string]: any;
}

export interface ExcelValidationError {
  row: number;
  field: string;
  message: string;
  value?: any;
}

class ExcelParserService {
  // Expected column headers mapping
  private readonly columnMapping: Record<string, keyof SchoolCreateRequest> = {
    'Tên trường': 'name',
    'Mã trường': 'code',
    'Loại trường': 'type',
    'Cấp học': 'level',
    'Địa chỉ': 'address',
    'Quận/Huyện': 'district',
    'Tỉnh/Thành phố': 'city',
    'Số điện thoại': 'phone',
    'Email': 'email',
    'Website': 'website',
    'Hiệu trưởng': 'principal',
    'Năm thành lập': 'foundedYear',
    'Số học sinh': 'studentCount',
    'Số giảng viên': 'teacherCount',
  };

  // Alternative column names for flexibility
  private readonly alternativeMappings: Record<string, keyof SchoolCreateRequest> = {
    'Tên trường học': 'name',
    'Tên': 'name',
    'Name': 'name',
    'Mã trường học': 'code',
    'Mã': 'code',
    'Code': 'code',
    'Loại hình': 'type',
    'Type': 'type',
    'Cấp độ': 'level',
    'Level': 'level',
    'Địa chỉ trường': 'address',
    'Address': 'address',
    'Quận': 'district',
    'Huyện': 'district',
    'District': 'district',
    'Tỉnh': 'city',
    'Thành phố': 'city',
    'Province': 'city',
    'City': 'city',
    'Điện thoại': 'phone',
    'Phone': 'phone',
    'Số điện thoại': 'phone',
    'Email trường': 'email',
    'Website trường': 'website',
    'Tên hiệu trưởng': 'principal',
    'Principal': 'principal',
    'Năm thành lập': 'foundedYear',
    'Founded Year': 'foundedYear',
    'Học sinh': 'studentCount',
    'Students': 'studentCount',
    'Giảng viên': 'teacherCount',
    'Teachers': 'teacherCount',
  };

  /**
   * Parse Excel file and extract school data
   */
  async parseExcelFile(file: File): Promise<ExcelParseResult> {
    try {
      const workbook = await this.readExcelFile(file);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      if (!worksheet) {
        return {
          success: false,
          data: [],
          errors: ['Không tìm thấy sheet nào trong file Excel'],
          warnings: []
        };
      }

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '',
        blankrows: false 
      }) as any[][];

      if (jsonData.length < 2) {
        return {
          success: false,
          data: [],
          errors: ['File Excel không có dữ liệu hoặc chỉ có header'],
          warnings: []
        };
      }

      // Extract headers and data
      const headers = jsonData[0];
      const dataRows = jsonData.slice(1);

      // Map headers to field names
      const fieldMapping = this.mapHeadersToFields(headers);
      
      // Parse and validate data
      const result = this.parseDataRows(dataRows, fieldMapping, headers);

      return result;
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      return {
        success: false,
        data: [],
        errors: [`Lỗi khi đọc file Excel: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`],
        warnings: []
      };
    }
  }

  /**
   * Read Excel file using XLSX library
   */
  private async readExcelFile(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Không thể đọc file'));
      };
      
      reader.readAsBinaryString(file);
    });
  }

  /**
   * Map Excel headers to field names
   */
  private mapHeadersToFields(headers: string[]): Record<number, keyof SchoolCreateRequest> {
    const mapping: Record<number, keyof SchoolCreateRequest> = {};
    
    headers.forEach((header, index) => {
      if (!header) return;
      
      const normalizedHeader = header.trim();
      
      // Check exact mapping first
      if (this.columnMapping[normalizedHeader]) {
        mapping[index] = this.columnMapping[normalizedHeader];
        return;
      }
      
      // Check alternative mappings
      if (this.alternativeMappings[normalizedHeader]) {
        mapping[index] = this.alternativeMappings[normalizedHeader];
        return;
      }
      
      // Try case-insensitive matching
      const lowerHeader = normalizedHeader.toLowerCase();
      for (const [key, value] of Object.entries(this.alternativeMappings)) {
        if (key.toLowerCase() === lowerHeader) {
          mapping[index] = value;
          return;
        }
      }
    });
    
    return mapping;
  }

  /**
   * Parse data rows and validate
   */
  private parseDataRows(
    dataRows: any[][], 
    fieldMapping: Record<number, keyof SchoolCreateRequest>,
    headers: string[]
  ): ExcelParseResult {
    const schools: SchoolCreateRequest[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];
    const unknownLevelRows = new Map<string, number[]>();

    dataRows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 2; // +2 because we skip header and 0-indexed
      
      // Skip empty rows
      if (row.every(cell => !cell || cell.toString().trim() === '')) {
        return;
      }

      const school: Partial<SchoolCreateRequest> = {};
      const rowErrors: string[] = [];

      // Map data to school object
      Object.entries(fieldMapping).forEach(([colIndex, fieldName]) => {
        const value = row[parseInt(colIndex)];
        const processedValue = this.processFieldValue(fieldName, value);
        
        if (processedValue !== undefined && processedValue !== '') {
          (school as any)[fieldName] = processedValue;
        }
      });

      // Validate required fields
      this.validateRequiredFields(school, rowNumber, rowErrors);
      
      // Validate field formats
      this.validateFieldFormats(school, rowNumber, rowErrors);

      // Cấp học lạ (vd "Liên cấp", "Mầm non") vẫn nhập được như trước, chỉ cảnh báo.
      if (school.level && !isStandardSchoolLevel(school.level)) {
        const rows = unknownLevelRows.get(school.level) ?? [];
        unknownLevelRows.set(school.level, [...rows, rowNumber]);
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      } else {
        schools.push(school as SchoolCreateRequest);
      }
    });

    // Gộp theo giá trị: file "Liên cấp" thường lặp cùng một giá trị trên mọi dòng.
    unknownLevelRows.forEach((rows, level) => {
      const shown = rows.slice(0, MAX_LISTED_ROWS).join(', ');
      const more = rows.length > MAX_LISTED_ROWS ? ', …' : '';
      warnings.push(
        `Cấp học "${level}" không thuộc danh sách chuẩn, sẽ lưu nguyên giá trị (${rows.length} dòng: ${shown}${more})`
      );
    });

    // Add warnings for missing columns
    const missingColumns = this.getMissingRequiredColumns(fieldMapping);
    if (missingColumns.length > 0) {
      warnings.push(`Các cột bắt buộc bị thiếu: ${missingColumns.join(', ')}`);
    }

    return {
      success: errors.length === 0,
      data: schools,
      errors,
      warnings
    };
  }

  /**
   * Process field value based on field type
   */
  private processFieldValue(fieldName: keyof SchoolCreateRequest, value: any): any {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const stringValue = value.toString().trim();

    switch (fieldName) {
      case 'foundedYear':
      case 'studentCount':
      case 'teacherCount':
        const numValue = parseInt(stringValue);
        return isNaN(numValue) ? undefined : numValue;
      
      case 'email':
        return stringValue.toLowerCase();
      
      case 'code':
        return stringValue.toUpperCase();

      // Không nhận ra thì giữ nguyên chữ người dùng nhập.
      case 'level':
        return normalizeSchoolLevel(stringValue) ?? stringValue;

      default:
        return stringValue;
    }
  }

  /**
   * Validate required fields
   */
  private validateRequiredFields(school: Partial<SchoolCreateRequest>, rowNumber: number, errors: string[]): void {
    const requiredFields: (keyof SchoolCreateRequest)[] = [
      'name', 'code', 'type', 'level', 'address', 'principal', 'foundedYear'
    ];

    requiredFields.forEach(field => {
      if (!school[field] || (typeof school[field] === 'string' && school[field]!.toString().trim() === '')) {
        errors.push(`Dòng ${rowNumber}: Thiếu thông tin bắt buộc "${this.getFieldDisplayName(field)}"`);
      }
    });
  }

  /**
   * Validate field formats
   */
  private validateFieldFormats(school: Partial<SchoolCreateRequest>, rowNumber: number, errors: string[]): void {
    // Validate email format
    if (school.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(school.email)) {
      errors.push(`Dòng ${rowNumber}: Email không hợp lệ "${school.email}"`);
    }

    // Validate phone format
    if (school.phone && !/^[\d\s\-+()]+$/.test(school.phone)) {
      errors.push(`Dòng ${rowNumber}: Số điện thoại không hợp lệ "${school.phone}"`);
    }

    // Validate founded year
    if (school.foundedYear && (school.foundedYear < 1800 || school.foundedYear > new Date().getFullYear())) {
      errors.push(`Dòng ${rowNumber}: Năm thành lập không hợp lệ "${school.foundedYear}"`);
    }

    // Validate numeric fields
    if (school.studentCount !== undefined && school.studentCount < 0) {
      errors.push(`Dòng ${rowNumber}: Số học sinh không hợp lệ "${school.studentCount}"`);
    }

    if (school.teacherCount !== undefined && school.teacherCount < 0) {
      errors.push(`Dòng ${rowNumber}: Số giảng viên không hợp lệ "${school.teacherCount}"`);
    }
  }

  /**
   * Get missing required columns
   */
  private getMissingRequiredColumns(fieldMapping: Record<number, keyof SchoolCreateRequest>): string[] {
    const requiredFields: (keyof SchoolCreateRequest)[] = [
      'name', 'code', 'type', 'level', 'address', 'principal', 'foundedYear'
    ];

    const mappedFields = Object.values(fieldMapping);
    return requiredFields.filter(field => !mappedFields.includes(field));
  }

  /**
   * Get field display name for error messages
   */
  private getFieldDisplayName(field: keyof SchoolCreateRequest): string {
    const displayNames: Record<keyof SchoolCreateRequest, string> = {
      name: 'Tên trường',
      code: 'Mã trường',
      type: 'Loại trường',
      level: 'Cấp học',
      address: 'Địa chỉ',
      district: 'Quận/Huyện',
      city: 'Tỉnh/Thành phố',
      districtCode: 'Mã quận/huyện',
      cityCode: 'Mã tỉnh/thành phố',
      phone: 'Số điện thoại',
      email: 'Email',
      website: 'Website',
      principal: 'Hiệu trưởng',
      foundedYear: 'Năm thành lập',
      studentCount: 'Số học sinh',
      teacherCount: 'Số giảng viên',
      metadata: 'Metadata'
    };

    return displayNames[field] || field;
  }

  /**
   * Generate Excel template
   */
  generateTemplate(): Blob {
    const templateData = [
      [
        'Tên trường', 'Mã trường', 'Loại trường', 'Cấp học', 'Địa chỉ',
        'Quận/Huyện', 'Tỉnh/Thành phố', 'Số điện thoại', 'Email', 'Website',
        'Hiệu trưởng', 'Năm thành lập', 'Số học sinh', 'Số giảng viên'
      ],
      [
        'Trường THPT Nguyễn Du', 'THPT001', 'public', 'high', '123 Đường ABC',
        'Quận 1', 'TP. Hồ Chí Minh', '0281234567', 'info@nguyendu.edu.vn', 'https://nguyendu.edu.vn',
        'Nguyễn Văn A', '1990', '500', '30'
      ]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Danh sách trường học');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }
}

export const excelParserService = new ExcelParserService();
